import { useQueryClient } from '@tanstack/react-query'
import { useNotificationsRealtime, type RealtimeEvent } from './useNotificationsRealtime'
import { useTypingStore } from '@/store/typing'
import { CHAVE_PEDIDO_TERRAFORM } from './useTerraformChangeRequest'

/**
 * useChatRealtime — tempo real do chat/alertas via SSE (feed unificado).
 *
 * Wrapper fino sobre useNotificationsRealtime: NÃO abre conexão nova — reusa a
 * conexão SSE do feed de notificações (SharedWorker singleton por URL) apenas
 * pelo callback onEvent, demultiplexando os eventos de chat:
 *  - message.created → invalida as listas (['conversations'] + ['chat-human-conversations'])
 *      + ['conversation-messages', cid] + ['terraform-change-request']
 *  - message.updated / message.deleted / read → invalida ['conversation-messages', cid] + as listas
 *  - reaction.updated → invalida só ['conversation-messages', cid]
 *
 * (Substituiu o WebSocket, removido na Fase 3 WS→SSE.)
 */
export function useChatRealtime(): void {
  const qc = useQueryClient()

  useNotificationsRealtime(
    {},
    {
      onEvent: (evt: RealtimeEvent & { conversation_id?: number; user_id?: number; user_name?: string }) => {
        const cid = evt.conversation_id
        if (evt.type === 'chat.typing') {
          // Presença efêmera — alimenta o store (o indicador filtra por frescor
          // e ignora o próprio usuário). Não invalida query nenhuma.
          if (cid != null && evt.user_id != null) {
            useTypingStore.getState().setTyping(cid, evt.user_id, evt.user_name ?? 'Alguém')
          }
          return
        }
        // Invalida AS DUAS listas de conversas (alertas + DMs/grupos). Antes só
        // ['conversations'] era invalidada → a lista humana (ConversasTab) ficava
        // estática: não reordenava, não bumpava badge nem atualizava o preview.
        const invalidateLists = () => {
          void qc.invalidateQueries({ queryKey: ['conversations'] })
          void qc.invalidateQueries({ queryKey: ['chat-human-conversations'] })
        }
        if (evt.type === 'conversations.changed') {
          // Membership de Organização mudou (entrou/saiu de Org) → revalida a
          // lista de conversas na hora: o grupo da Org aparece/some sem F5.
          invalidateLists()
          return
        }
        if (evt.type === 'message.created') {
          invalidateLists()
          if (cid != null) {
            void qc.invalidateQueries({ queryKey: ['conversation-messages', cid] })
          }
          // D11: o worker avisa a conversa em cada marco do pedido de
          // Terraform — o mesmo evento relê o cartão do pedido.
          void qc.invalidateQueries({ queryKey: [CHAVE_PEDIDO_TERRAFORM] })
        } else if (
          cid != null &&
          (evt.type === 'message.updated' ||
            evt.type === 'message.deleted' ||
            evt.type === 'read')
        ) {
          // updated/deleted mudam o preview; read muda o badge de não-lidas →
          // refletir nas listas também, não só no thread aberto.
          void qc.invalidateQueries({ queryKey: ['conversation-messages', cid] })
          invalidateLists()
        } else if (cid != null && evt.type === 'reaction.updated') {
          // Reação não afeta lista/preview — só o thread aberto.
          void qc.invalidateQueries({ queryKey: ['conversation-messages', cid] })
        }
      },
    },
  )
}
